/**
 * appUpdate.js — detect when a newer build has been deployed and apply it,
 * working for BOTH an installed PWA and a plain browser tab.
 *
 * Two detection paths, used together:
 *  1. Service worker: a newly-installed-but-waiting worker means a new version
 *     is ready. We don't auto-activate it — the UI prompts the user, then
 *     `applyUpdate` tells it to take over (SKIP_WAITING) and the page reloads.
 *  2. Build-manifest poll: fetch /asset-manifest.json (no-store) and compare the
 *     entrypoint hash to the one loaded at startup. Covers browsers where no SW
 *     is active and catches a new deploy even before the SW notices.
 */

/** True when running as an installed/standalone app (vs a browser tab). */
export const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true)) || false;

const isProd = process.env.NODE_ENV === "production";

// `reloading` only stops a second reload within ONE page life — it is reset by
// the very reload it fires. That is no protection against a reload *loop*,
// where each fresh page immediately takes another controllerchange and goes
// again. `controllerchange` normally fires only after the user accepts an
// update, but it also fires in every other open tab when one tab accepts, so
// the event is not as singular as it looks — and a reload loop is unrecoverable
// from the user's side, since no page ever stays up long enough to click
// anything. Bound it in sessionStorage, which survives the reload.
const RELOAD_LOG_KEY = "vitrine_sw_reloads";
const RELOAD_LIMIT = 3;
const RELOAD_WINDOW_MS = 30_000;

let reloading = false;
const reloadOnce = () => {
  if (reloading) return;

  try {
    const now = Date.now();
    const recent = JSON.parse(sessionStorage.getItem(RELOAD_LOG_KEY) || "[]")
      .filter((t) => now - t < RELOAD_WINDOW_MS);

    if (recent.length >= RELOAD_LIMIT) {
      console.error(
        `[appUpdate] ${recent.length} service-worker reloads in ` +
          `${RELOAD_WINDOW_MS / 1000}s — stopping to avoid a reload loop. ` +
          "The app may be running an older build until the next manual refresh."
      );
      return;
    }

    recent.push(now);
    sessionStorage.setItem(RELOAD_LOG_KEY, JSON.stringify(recent));
  } catch {
    /* private mode / storage disabled — fall through and reload once */
  }

  reloading = true;
  window.location.reload();
};

/**
 * Register the service worker and notify when a new version is waiting.
 * @param {(reg: ServiceWorkerRegistration) => void} onUpdateReady
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker(onUpdateReady) {
  if (!isProd || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js");

    // Reload once the new SW takes control (after the user accepts).
    navigator.serviceWorker.addEventListener("controllerchange", reloadOnce);

    // Already waiting (e.g. updated in another tab)?
    if (reg.waiting && navigator.serviceWorker.controller) onUpdateReady(reg);

    reg.addEventListener("updatefound", () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        // "installed" + an existing controller = an update (not first install).
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          onUpdateReady(reg);
        }
      });
    });

    return reg;
  } catch {
    return null;
  }
}

/** Apply a waiting service-worker update (then controllerchange reloads). */
export function applyUpdate(reg) {
  if (reg && reg.waiting) {
    reg.waiting.postMessage({ type: "SKIP_WAITING" });
    // Safety net if controllerchange doesn't fire promptly.
    setTimeout(reloadOnce, 1500);
  } else {
    reloadOnce();
  }
}

/** Ask the browser to re-check the SW for a newer version. */
export function checkServiceWorkerUpdate(reg) {
  reg?.update?.().catch(() => {});
}

// ── Build-manifest version check ─────────────────────────────────────────────
async function fetchEntrypoint() {
  try {
    const res = await fetch("/asset-manifest.json", { cache: "no-store" });
    if (!res.ok) return null;
    const m = await res.json();
    // CRA emits { entrypoints: [...], files: {...} }; the entrypoints array
    // contains content-hashed filenames that change on every build.
    return JSON.stringify(m.entrypoints || m.files?.["main.js"] || m);
  } catch {
    return null;
  }
}

let baselineEntrypoint = null;

/** Record the build that is currently running (call once at startup). */
export async function captureBuildBaseline() {
  baselineEntrypoint = await fetchEntrypoint();
}

/** @returns {Promise<boolean>} whether a newer build is now deployed. */
export async function isNewBuildDeployed() {
  if (baselineEntrypoint == null) return false; // baseline unknown → don't false-alarm
  const current = await fetchEntrypoint();
  return current != null && current !== baselineEntrypoint;
}
